import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  costOf,
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  type Centavos,
} from '@abastece/shared';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { emitir } from '../lib/emitir.js';
import { usePuede } from '../lib/sesion.js';
import { COLOR, ETIQUETA, tablero } from '../lib/vencimientos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { useHoy } from '../lib/hoy.js';

/**
 * The expiry board.
 *
 * Reads the batch data that has been quietly accumulating since the first purchase. This is
 * where medicine and perishable tracking stops being paperwork and starts being the thing that
 * keeps expired product off the shelf — and answers "which batch" when a recall comes.
 */
export function Vencimientos() {
  const t = useT();
  const nombre = useNombreProducto();
  const lotes = useLiveQuery(() => db.lotes.toArray(), [], []);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const puede = usePuede('ajustar_inventario');
  /** What is at risk in money is the books' cost of it: said to the owner, not to a cashier. */
  const verCostos = usePuede('ver_costos');
  const [dandoBaja, setDandoBaja] = useState<string | null>(null);

  const porId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);
  // Today as it turns (`useHoy`): left open overnight, a batch that expired at midnight still read as good.
  const dia = useHoy();
  const filas = useMemo(
    () => tablero(lotes, config.vencimientoAlertaDias, dia),
    [lotes, config.vencimientoAlertaDias, dia],
  );

  const vencidos = filas.filter((f) => f.estado === 'vencido');
  const valorEnRiesgo = filas.reduce((a, f) => {
    const e = existencias.find((x) => x.id === existenciaId(f.productoId, f.ubicacion));
    return a + (e ? costOf(e.costoPromedio, f.cantidadDisponible) : 0);
  }, 0) as Centavos;

  async function darDeBaja(loteRowId: string) {
    const lote = lotes.find((l) => l.id === loteRowId);
    if (!lote) return;
    setDandoBaja(loteRowId);
    await emitir('inventario.salida', {
      salidaId: newId(),
      ubicacion: lote.ubicacion,
      fecha: hoy(),
      productoId: lote.productoId,
      cantidadBase: lote.cantidadDisponible,
      motivo: 'vencido',
      loteId: lote.id,
      nota: t('venc.notaBaja', { numero: lote.numero, fecha: lote.vencimiento }),
    });
    setDandoBaja(null);
  }

  return (
    <Pagina
      titulo={t('venc.titulo')}
      subtitulo={
        filas.length === 0
          ? undefined
          : verCostos
            ? t('venc.subtitulo', { n: filas.length, monto: formatMoney(valorEnRiesgo) })
            : t('venc.lotes', { n: filas.length })
      }
      volverA="/inventario"
    >
      {vencidos.length > 0 && (
        <p className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {t('venc.yaVencidos', { n: vencidos.length })}
        </p>
      )}

      {filas.length === 0 ? (
        <Vacio mensaje={t('venc.ninguno', { dias: config.vencimientoAlertaDias })} icono="vencimientos" />
      ) : (
        <ul className="flex flex-col gap-2">
          {filas.map((f) => {
            const producto = porId.get(f.productoId);
            return (
              <li key={f.id} className="tarjeta">
                <div className="flex items-start gap-3">
                  {producto && <Miniatura producto={producto} tamano="sm" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {producto ? nombre(producto) : t('pos.producto')}
                    </p>
                    <p className="tabular mt-0.5 text-xs text-slate-500">
                      {t('venc.lote', {
                        numero: f.numero,
                        cantidad: formatQuantity(f.cantidadDisponible),
                        unidad: producto ? etiquetaUnidad(producto.unidadBase, producto.unidadBase) : '',
                      })}
                      {/* The place only where it is not the shop, the one there is: a lot left elsewhere says where. */}
                      {f.ubicacion !== 'tienda' && ` · ${etiquetaLugar(f.ubicacion)}`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-lg px-2.5 py-1 text-xs font-medium ${COLOR[f.estado]}`}
                  >
                    {t(ETIQUETA[f.estado])}
                  </span>
                </div>

                <p className="tabular mt-2 text-xs text-slate-500">
                  {t('venc.vence', { fecha: f.vencimiento })}
                  {f.dias >= 0
                    ? t('venc.enDias', { n: f.dias })
                    : t('venc.haceDias', { n: -f.dias })}
                </p>

                {puede && (
                  <button
                    type="button"
                    className="btn-texto mt-3 gap-1 text-xs text-slate-400"
                    disabled={dandoBaja === f.id}
                    onClick={() => void darDeBaja(f.id)}
                  >
                    <Ico icono="merma" className="size-3.5" />
                    {t(dandoBaja === f.id ? 'venc.registrando' : 'venc.darDeBaja')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Pagina>
  );
}
